<template>
  <div>
    <q-slide-transition>
      <q-footer
        v-if="session.sessionPrograms.length > 0"
        elevated
        bordered
        class="bg-white text-black"
      >
        <div
          class="bg-grey-2 text-grey-6 cursor-pointer pull-handle z-top"
          @click="minimized = !minimized"
        >
          <q-icon name="drag_handle" />
        </div>
        <q-toolbar class="q-px-none z-top">
          <div
            style="height:auto;"
            class="bg-grey-2 full-width"
          >
            <div
              class="full-width row q-mt-sm full-height items-center"
            >
              <div class="col text-center q-pa-none">
                <p
                  class="q-ma-none q-py-none q-px-md ellipsis"
                  :class="minimized ? 'text-subtitle2' : 'text-h6'"
                >
                  {{ sessionName }}
                </p>
              </div>
              <div class="col-auto q-pr-md">
                <div>
                  <q-btn-dropdown
                    :label="$t('sessions.sessionBuilder.saveSessionLabel')"
                    color="primary"
                    icon="schedule"
                    split
                    :size="minimized?'xs':'sm'"
                    class="q-mb-xs"
                    data-e2e="button-save-session"
                    @click="onScheduleSession"
                  >
                    <q-list>
                      <q-item
                        v-if="!hidePlayButton"
                        v-close-popup
                        clickable
                        :disable="!userCanStartSession"
                        class="text-dark"
                        @click="onRunSession"
                      >
                        <q-item-section side>
                          <q-icon
                            name="play_arrow"
                          />
                        </q-item-section>
                        <q-item-section>
                          {{ $t('sessions.sessionBuilder.textRunPrograms') }}
                          <q-tooltip
                            v-if="!userCanStartSession"
                          >
                            {{ $t('sessions.sessionCard.disabledStartSession') }}
                          </q-tooltip>
                        </q-item-section>
                      </q-item>
                      <q-item
                        v-if="canAddManualSessionEntry"
                        v-close-popup
                        clickable
                        class="text-dark"
                        :disable="!userCanStartSession"
                        @click="onManualSessionEntry"
                      >
                        <q-item-section side>
                          <q-icon
                            name="add"
                          />
                        </q-item-section>
                        <q-item-section>
                          {{ $t('sessions.sessionCard.textAddManualSessionEntry') }}
                          <q-tooltip
                            v-if="!userCanStartSession"
                          >
                            {{ $t('sessions.sessionCard.disabledStartSession') }}
                          </q-tooltip>
                        </q-item-section>
                      </q-item>
                      <q-item
                        v-close-popup
                        clickable
                        class="text-dark"
                        @click="onCancelChanges"
                      >
                        <q-item-section side>
                          <q-icon
                            name="cancel"
                          />
                        </q-item-section>
                        <q-item-section>
                          {{ $t('sessions.sessionBuilder.textCancel') }}
                        </q-item-section>
                      </q-item>
                    </q-list>
                  </q-btn-dropdown>
                </div>
              </div>
            </div>
            <div class="full-width q-pt-none q-mt-none">
              <q-scroll-area
                ref="sessionBuilderScroll"
                class="bg-grey-2 session-builder-inner full-width"
                :class="{ minimized }"
              >
                <div
                  class="row no-wrap q-mx-xs"
                >
                  <draggable
                    v-bind="dragOptions"
                    v-model="pinnedSessionPrograms"
                    class="pinned-list-group row no-wrap q-my-xs"
                    tag="span"
                    group="pinned"
                    :component-data="!pinnedDrag ? { name: 'flip-list' } : null"
                    item-key="order"
                    @start="pinnedDrag = true"
                    @end="drag = false"
                  >
                    <template #item="{ element: sessionProgram, index }">
                      <session-program-card
                        :ref="sessionProgram.program.id"
                        :session-program="sessionProgram"
                        :index="index"
                        @change-target-status-selected="onChangeTargetStatusSelected"
                        @remove="onRemoveSessionProgram(sessionProgram)"
                        @unpin="onUnpin(sessionProgram)"
                      />
                    </template>
                  </draggable>
                  <q-separator
                    v-if="!noSessionProgramsPinned"
                    vertical
                    inset
                    class="q-mx-sm"
                  />
                  <draggable
                    v-bind="dragOptions"
                    v-model="orderedSessionPrograms"
                    class="list-group row no-wrap q-my-xs"
                    tag="span"
                    group="unpinned"
                    item-key="order"
                    @start="drag = true"
                    @end="drag = false"
                  >
                    <template #item="{ element: sessionProgram, index }">
                      <session-program-card
                        :ref="sessionProgram.program.id"
                        :session-program="sessionProgram"
                        :class="{ 'cursor-move': drag }"
                        :pin-available="canPinSessionProgram(sessionProgram)"
                        :show-move-to="hasManySessionPrograms"
                        :index="index"
                        @change-target-status-selected="onChangeTargetStatusSelected"
                        @remove="onRemoveSessionProgram(sessionProgram)"
                        @pin="onPin(sessionProgram)"
                        @hide="onHide(sessionProgram)"
                        @unhide="onUnhide(sessionProgram)"
                        @move-to="onMoveTo(sessionProgram)"
                      />
                    </template>
                  </draggable>
                </div>
              </q-scroll-area>
            </div>
          </div>
        </q-toolbar>
      </q-footer>
    </q-slide-transition>
  </div>
</template>
<script>
import EditSessionActivityDialog from 'components/SessionActivities/Modals/EditSessionActivityDialog';
import ScheduleSession from 'components/Sessions/Modals/ScheduleSession';
import SessionProgramCard from 'components/Sessions/SessionProgramCard';
import { Timestamp } from 'firebase/firestore';
import {
  chain, concat, findIndex, forEach, get, isEmpty,
  maxBy, reject, set, size,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import SessionsDB from 'src/fb/sessions-db';
import Auth from 'src/mixins/Auth';
import Interactions from 'src/mixins/Interactions';
import ProgramsMixin from 'src/mixins/ProgramsMixin';
import { auth } from 'src/store/auth';
import { sessions } from 'src/store/sessions';
import { defineAsyncComponent } from 'vue';
import draggable from 'vuedraggable';

export default {
  name: 'SessionBuilder',

  components: { draggable, SessionProgramCard },

  mixins: [Auth, ProgramsMixin, Interactions],

  props: {
    client: {
      type: String,
      default: null,
    },
    clinic: {
      type: String,
      default: null,
    },
    package: {
      type: String,
      default: null,
    },
    hidePlayButton: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['session-changed', 'session-saved'],

  data() {
    return {
      minimized: false,
      pinnedDrag: false,
      drag: false,

      session: { sessionPrograms: [] },
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'currentClient']),

    sessionName() {
      return this.session.sessionName || this.$t('sessions.sessionBuilder.textTitle');
    }, // sessionName()

    noSessionProgramsPinned() {
      return (isEmpty(this.pinnedSessionPrograms));
    }, // noSessionProgramsPinned()

    pinnedSessionPrograms: {
      get() {
        return chain(this.session.sessionPrograms).filter({ pinned: true }).orderBy('order').value();
      },
      set(reorderedPinnedSessionPrograms) {
        // Set order property according to order of array
        this.session.sessionPrograms = concat(
          reorderedPinnedSessionPrograms
            .map((sessionProgram, index) => ({
              ...sessionProgram,
              order: index + 1,
            })),
          this.orderedSessionPrograms,
        );
      },
    }, // pinnedSessionPrograms()

    orderedSessionPrograms: {
      get() {
        return chain(this.session.sessionPrograms).omitBy('pinned').orderBy('order').value();
      },
      set(reorderedSessionPrograms) {
        // Set order property according to order of array
        this.session.sessionPrograms = concat(
          this.pinnedSessionPrograms,
          reorderedSessionPrograms
            .map((sessionProgram, index) => ({
              ...sessionProgram,
              order: index + 1,
            })),
        );
      },
    }, // orderedSessionPrograms()

    hasManySessionPrograms() {
      return size(this.orderedSessionPrograms) > 5;
    }, // hasManySessionPrograms()

    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost',
        direction: 'horizontal',
        filter: '.q-btn, .q-chip',
        preventOnFilter: false,
      };
    },

    userCanStartSession() {
      return this.userHasPrivilege('sessions/startSessions');
    }, // userCanStartSession()

    canAddManualSessionEntry() {
      return this.userHasPrivilege('sessions/enterManualSessions');
    }, // canAddManualSessionEntry()

  },

  watch: {
    session: {
      deep: true,
      immediate: true,
      handler() {
        // @todo: probably move it to corresponding methods instead of watch?
        this.$emit('session-changed', this.session);
      },
    },
  },

  methods: {
    ...mapActions(sessions, ['loadSessionPrograms']),

    onScheduleSession() {
      this.openComponentDialog(
        ScheduleSession,
        {
          value: this.session,
        },
        () => {
          this.clear();
          this.$emit('session-saved');
        },
      );
    }, // onScheduleSession()

    async onManualSessionEntry() {
      const startTimestamp = Timestamp.fromDate(new Date(Date.now() - 60 * 60 * 1000));
      const endTimestamp = Timestamp.fromDate(new Date(Date.now()));
      const sessionActivity = {
        startTimestamp,
        endTimestamp,
        startGeolocation: null,
        endGeolocation: null,
        status: 'finished',
        type: 'session',
        user: this.userDetails.userId,
        userName: this.userDetails.name,
        client: this.client,
        clinic: this.clinic,
        notes: '',
        sessionDurationOffset: 0,
        sessionNoteTemplateId: null,
        isDeleted: false,
      };

      // Show session activity timing dialog
      this.openComponentDialog(EditSessionActivityDialog, {
        sessionActivity,
        title: this.$t('sessions.sessionCard.textAddManualSessionEntry'),
      }, async (sessionActivityChanges) => {
        if ((!(get(sessionActivityChanges, 'startTimestamp') instanceof Timestamp))
          || (!(get(sessionActivityChanges, 'endTimestamp') instanceof Timestamp))) {
          return;
        }
        const newSessionActivity = {
          ...sessionActivity,
          ...sessionActivityChanges,
        };
        const duration = (
          newSessionActivity.endTimestamp.toDate()
          - newSessionActivity.startTimestamp.toDate()
        ) / 1000;
        newSessionActivity.sessionDurationEnd = duration;

        // Duplicate and make session ready
        this.$q.loading.show();

        const sessionInfo = {
          createdBy: this.userDetails.userId,
          client: this.client,
          clinic: this.clinic,

          runBy: newSessionActivity.user,
          runByName: newSessionActivity.userName,
          runTimestamp: newSessionActivity.startTimestamp,
          finishTimestamp: newSessionActivity.endTimestamp,
          supervisedBy: null,
          supervisedByName: null,
          duration,
          hasTimecodedData: true,
          status: 'done',
          version: 2,
          isDeleted: false,
        };
        try {
          const sessionsDb = new SessionsDB();
          const newSessionId = await sessionsDb
            .createSession(sessionInfo, this.session.sessionPrograms);
          const sessionActivitiesDb = new SessionActivitiesDB();

          await sessionActivitiesDb.create({
            ...newSessionActivity,
            client: this.session.client,
            clinic: this.session.clinic,
            session: newSessionId,
            sessionName: this.session.sessionName || null,
            appointment: null,
            createdBy: this.userDetails.userId,
          });

          // // Redirect to history
          this.clear();
          await this.$router.push(`/history/${newSessionId}/edit`);
        } finally {
          this.$q.loading.hide();
        }
      });
    },

    async onRunSession() {
      this.$q.loading.show();
      const sessionInfo = {
        client: this.client,
        clinic: this.clinic,
        package: this.package,
        createdBy: this.userDetails.userId,
        version: 2,
        isDeleted: false,
      };

      const sessionsDb = new SessionsDB();
      try {
        const sessionId = await sessionsDb
          .createSession(sessionInfo, this.session.sessionPrograms);
        this.clear();
        this.$router.push(`/run/${sessionId}/overview`);
      } finally {
        this.$q.loading.hide();
      }
    },

    onCancelChanges() {
      this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        this.$t('sessions.sessionBuilder.confirmCancelChanges'),
        () => {
          this.clear();
        },
      );
    },

    onRemoveSessionProgram(sessionProgram) {
      const originalIndex = findIndex(
        this.session.sessionPrograms,
        sessionProgram,
      );

      this.session.sessionPrograms.splice(originalIndex, 1);
      if (this.orderedSessionPrograms.length === 0) {
        // Unpin programs to prevent that we end up with an empty session and a pinned program
        this.unpinSessionPrograms();
      }
      if (this.session.sessionPrograms.length === 0) {
        this.clear();
      }
    },

    onPin(pinSessionProgram) {
      this.orderedSessionPrograms = reject(this.orderedSessionPrograms, pinSessionProgram);
      this.pinnedSessionPrograms = [...this.pinnedSessionPrograms, { ...pinSessionProgram, pinned: true }];
    },

    onUnpin(unpinSessionProgram) {
      this.pinnedSessionPrograms = reject(this.pinnedSessionPrograms, unpinSessionProgram);
      this.orderedSessionPrograms = [
        { ...unpinSessionProgram, pinned: false, order: 0 },
        ...this.orderedSessionPrograms,
      ];
    },

    onHide(hideSessionProgram) {
      const originalIndex = findIndex(
        this.orderedSessionPrograms,
        hideSessionProgram,
      );
      if (originalIndex !== -1) {
        this.orderedSessionPrograms[originalIndex].hidden = true;
      }
    },

    onUnhide(unhideSessionProgram) {
      const originalIndex = findIndex(
        this.orderedSessionPrograms,
        unhideSessionProgram,
      );
      if (originalIndex !== -1) {
        this.orderedSessionPrograms[originalIndex].hidden = false;
      }
    }, // onUnhide()

    onMoveTo(movedSessionProgram) {
      const originalOrder = movedSessionProgram.order;
      if (!originalOrder) {
        return;
      }
      this.openComponentDialog(
        defineAsyncComponent(() => import('components/Sessions/Modals/MoveSessionProgram')),
        { sessionPrograms: this.orderedSessionPrograms },
        (moveToOrder) => {
          if (moveToOrder === originalOrder) {
            return;
          }
          const newOrder = moveToOrder > originalOrder ? moveToOrder - 1 : moveToOrder;

          // Move selected session program to the new order
          const orderStart = Math.min(originalOrder, newOrder);
          const orderEnd = Math.max(originalOrder, newOrder);
          const orderMove = newOrder > originalOrder ? -1 : 1;

          const reorderedSessionPrograms = chain(this.orderedSessionPrograms)
            .map((sessionProgram) => {
              let { order } = sessionProgram;

              if (order === originalOrder) {
                order = newOrder;
              } else if ((order >= orderStart) && (order <= orderEnd)) {
                order += orderMove;
              }
              return {
                ...sessionProgram,
                order,
              };
            })
            .sortBy('order')
            .value();

          this.session.sessionPrograms = concat(
            this.pinnedSessionPrograms,
            reorderedSessionPrograms,
          );
        },
      );
    }, // onMoveto()

    onChangeTargetStatusSelected(targetStatusInfo) {
      const sessionProgram = this.session.sessionPrograms
        .find((program) => program === targetStatusInfo.sessionProgram);
      if (sessionProgram) {
        sessionProgram.selectedTargets[targetStatusInfo.targetStatus] = targetStatusInfo.value;
      }
    }, // onChangeTargetStatusSelected()

    /**
     * Scrolls to the SessionProgramCard element based on original program id
     *
     * @param {string} originalProgramId The id for 'programs' collection
     */
    scrollToProgram(originalProgramId) {
      const programElement = this.$refs[originalProgramId];

      if (programElement) {
        const scrollDest = programElement.$el.offsetLeft + 150;
        this.$refs.sessionBuilderScroll.setScrollPosition('horizontal', scrollDest, 500);
      }
    },

    /**
     * Add a program to the session builder. Will be added as last program
     */
    add(program, preventDuplicate = false, duplicateMessage = false) {
      if (preventDuplicate) {
        if (chain(this.session)
          .get('sessionPrograms')
          .find({ program: { id: program.id } })
          .value()) {
          if (duplicateMessage) {
            this.notifyInfo(this.$t('programs.programCard.notifyProgramAlreadyInSession'));
          }

          return;
        }
      }

      let currentMaxOrder = 0;
      if (isEmpty(this.session.sessionPrograms)) {
        this.session.client = this.client;
        this.session.clinic = this.clinic;
        this.session.package = this.package;
      } else {
        currentMaxOrder = maxBy(this.session.sessionPrograms, 'order').order;
      }

      const sessionProgram = {
        session: null,
        programName: program.programName,
        status: 'created',
        program,
        selectedTargets: this.getDefaultTargets(program),
        pinned: false,
        hidden: false,
        order: currentMaxOrder + 1,
      };
      this.session.sessionPrograms.push(sessionProgram);

      this.$nextTick(() => {
        this.scrollToProgram(sessionProgram.program.id);
      });
    }, // add()

    remove(program) {
      this.session.sessionPrograms = reject(this.session.sessionPrograms, { program: { id: get(program, 'id') } });
    }, // remove()

    /**
   * Clear selected programs and session information
   */
    clear() {
      this.session = { sessionPrograms: [] };
    }, // clearSelectedPrograms()

    /**
     * Load a session and programs from the database
     */
    async load(session) {
      // @todo Maybe a transition to props would be a bit more by the book than letting
      // parent components call this load() function...
      this.$q.loading.show();
      try {
        const sessionPrograms = await this.loadSessionPrograms({
          session,
          allowLoadProgram: true,
        });
        this.session = {
          ...session,
          id: session.id,
          sessionPrograms,
        };
      } finally {
        this.$q.loading.hide();
      }
    }, // load()

    unpinSessionPrograms() {
      forEach(this.session.sessionPrograms, (sessionProgram, index) => {
        set(this.session.sessionPrograms[index], 'pinned', false);
      });
    }, // unpinSessionPrograms()

    canPinSessionProgram(sessionProgram) {
      // Must have at least two sessionprograms if we should be able to pin one of them
      return ((this.orderedSessionPrograms.length > 1)
        && !['asr'].includes(sessionProgram.program.type));
    }, // canPinSessionProgram()

  },
};
</script>
<style scoped>

.session-builder-inner.minimized {
height:16px;
}

.session-builder-inner {
height:30vh;

}

.pull-handle {
  position:absolute;
  top: -18px;
  width:40px;
  text-align:center;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 8px 8px 0px 0px;
  box-shadow:0 0 10px 2px rgba(0, 0, 0, 0.2), 0 0px 10px rgba(0, 0, 0, 0.24);

}

.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
.cursor-move {
  cursor: move;
}
</style>
